/**
 * Update Narration Titles Script
 * 
 * This script identifies duplicate narrations in the TTS Studio, verifies which ones
 * are mapped to Firebase documents, and updates the titles to be more meaningful
 * without Sentient branding.
 */

import { collection, getDocs, doc, updateDoc, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import { presentationDb } from '../firebase/config';
import { 
  getPhoneticText, 
  getNarrationTitle,
  type Narration,
  type SlideNarration,
  type NarrationStatus
} from './unifiedNarrationSync';
import { narrationScript } from '../data/narrationScript';

// Map of slide IDs to more meaningful titles
const titleReplacements: Record<string, string> = {
  // Replace SentientIQ branding with JIMI Wellness Platform
  'welcome-start': 'Welcome to JIMI Wellness Platform',
  'presenter-bio': 'Presenter Biography',
  'problem-statement': 'Clinician Burnout Challenge',
  'solution-overview': 'JIMI Wellness Platform Overview',
  'stanford-model': 'Stanford Professional Fulfillment Model',
  'adaptability-deepdive': 'Personal Resilience Tools',
  'culture-deepdive': 'Community & Culture Resources',
  'workplace-deepdive': 'Workplace Efficiency Tools',
  'ai-companion': 'JIMI AI Wellness Assistant',
  'implementation': 'Implementation Strategy',
  'timeline': 'Project Timeline',
  'cost-benefit': 'Cost-Benefit Analysis',
  'cost-benefit-conclusion': 'Conclusion & Next Steps',
  'q-and-a': 'Questions & Answers'
};

// Create a base narration object with required fields
const createBaseNarration = (
  id: string,
  title: string,
  text: string = '',
  type: 'slide' | 'dashboard' = 'slide'
): Narration => ({
  id,
  type,
  text,
  title,
  phoneticNarration: '',
  status: 'pending' as NarrationStatus,
  audioUrl: null,
  lastUpdated: new Date(),
  ...(type === 'slide' 
    ? { slideId: id } 
    : { dashboardId: id, script: '', triggerPoint: 'onLoad' as const })
});

/**
 * Check for duplicate narrations and update titles
 */
export const updateNarrationTitles = async (): Promise<{ success: boolean; totalUpdated: number }> => {
  try {
    // Get all narrations from Firebase
    const slideNarrationsRef = collection(presentationDb, 'slide_narrations');
    const snapshot = await getDocs(slideNarrationsRef);
    
    console.log(`Found ${snapshot.docs.length} narrations in Firebase`);
    console.log(`Found ${narrationScript.length} narrations in local script`);
    
    // Create a map of slideId to Firebase document IDs (to catch duplicates)
    const firebaseNarrationMap = new Map<string, string[]>();
    
    // First pass: identify duplicates
    snapshot.docs.forEach(docSnapshot => {
      const data = docSnapshot.data();
      if (data.slideId) {
        if (!firebaseNarrationMap.has(data.slideId)) {
          firebaseNarrationMap.set(data.slideId, []);
        }
        firebaseNarrationMap.get(data.slideId)!.push(docSnapshot.id);
      }
    });
    
    // Track statistics
    let updatedCount = 0;
    let createdCount = 0;
    let unchangedCount = 0;
    let duplicateCount = 0;
    let resolvedDuplicatesCount = 0;
    
    // Process each narration in the script
    for (const narration of narrationScript) {
      const { slideId, title, narration: narrationText } = narration;
      
      // Determine the new title
      let newTitle = titleReplacements[slideId] || title;
      
      // Remove any SentientIQ branding from titles
      if (newTitle.includes('SentientIQ')) {
        newTitle = newTitle.replace('SentientIQ', 'JIMI Wellness Platform');
      }
      
      // Check if this narration exists in Firebase
      if (firebaseNarrationMap.has(slideId)) {
        const docIds = firebaseNarrationMap.get(slideId)!;
        
        // Handle duplicate narrations
        if (docIds.length > 1) {
          console.log(`Found ${docIds.length} duplicates for slideId: ${slideId}`);
          duplicateCount += docIds.length - 1;
          
          // Sort by document ID to ensure consistent behavior
          docIds.sort();
          
          // Keep the first document and update it
          const keepDocId = docIds[0];
          const docRef = doc(presentationDb, 'slide_narrations', keepDocId);
          
          // Get the current document data
          const docSnap = snapshot.docs.find(d => d.id === keepDocId);
          const currentData = docSnap?.data() || {};
          
          // Generate phonetic text
          const phoneticNarration = getPhoneticText(narrationText);
          
          // Preserve the 'ready' status if it exists in any of the duplicates
          let status: NarrationStatus = 'pending';
          let audioUrl: string | undefined;
          
          // Check all duplicates for 'ready' status and audio URL
          for (const dupId of docIds) {
            const dupSnap = snapshot.docs.find(d => d.id === dupId);
            const dupData = dupSnap?.data() || {};
            if (dupData.status === 'ready') {
              status = 'ready';
            }
            if (dupData.audioUrl && !audioUrl) {
              audioUrl = dupData.audioUrl;
            }
          }
          
          // Update the document we're keeping
          await updateDoc(docRef, {
            title: newTitle,
            phoneticNarration,
            status,
            audioUrl,
            lastUpdated: new Date(),
            updatedAt: new Date().toISOString(),
            updatedSource: 'title-update-script'
          });
          
          console.log(`Updated primary narration ${slideId}: "${currentData.title}" -> "${newTitle}"`);
          updatedCount++;
          
          // Delete the duplicate documents (all except the first one)
          for (let i = 1; i < docIds.length; i++) {
            const deleteDocRef = doc(presentationDb, 'slide_narrations', docIds[i]);
            await deleteDoc(deleteDocRef);
            console.log(`Deleted duplicate narration with ID: ${docIds[i]}`);
            resolvedDuplicatesCount++;
          }
        } else {
          // No duplicates, just update the single document
          const docId = docIds[0];
          const docRef = doc(presentationDb, 'slide_narrations', docId);
          
          // Get the current document data
          const docSnap = snapshot.docs.find(d => d.id === docId);
          const currentData = docSnap?.data() || {};
          
          // Generate phonetic text
          const phoneticNarration = getPhoneticText(narrationText);
          
          // Only update if the title has changed
          if (currentData.title !== newTitle) {
            await updateDoc(docRef, {
              title: newTitle,
              phoneticNarration,
              lastUpdated: new Date(),
              updatedAt: new Date().toISOString(),
              updatedSource: 'title-update-script'
            });
            
            console.log(`Updated narration ${slideId}: "${currentData.title}" -> "${newTitle}"`);
            updatedCount++;
          } else {
            console.log(`Narration ${slideId} already has correct title: "${newTitle}"`);
            unchangedCount++;
          }
        }
      } else {
        // Narration doesn't exist in Firebase, create it
        console.log(`Creating new narration for slideId: ${slideId}`);
        
        const docRef = doc(collection(presentationDb, 'slide_narrations'));
        const phoneticNarration = getPhoneticText(narrationText);
        
        await setDoc(docRef, createBaseNarration(
          docRef.id,
          newTitle,
          narrationText,
          'slide'
        ));
        
        createdCount++;
      }
    }
    
    console.log('\nTitle update process completed:');
    console.log(`- Updated: ${updatedCount}`);
    console.log(`- Created: ${createdCount}`);
    console.log(`- Unchanged: ${unchangedCount}`);
    console.log(`- Duplicates found: ${duplicateCount}`);
    console.log(`- Duplicates resolved: ${resolvedDuplicatesCount}`);
    
    return { success: true, totalUpdated: updatedCount + createdCount };
  } catch (error) {
    console.error('Error updating narration titles:', error);
    return { success: false, totalUpdated: 0 };
  }
};
